import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { formatPayloadValue, DEPOSIT_PAYLOAD_ORDER } from "../../../helpers/activityLogHelpers";
import ActivityLogTimelineView from "../../../components/ActivityLog/ActivityLogTimelineView";

const BusinessActivityLog = ({ orgId, businessName, dealCategoryId }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs, loading, totalRecords } = useSelector((state) => state.activityLog);

  useEffect(() => {
    if (orgId) {
      dispatch(getActivityLog({ module: "BUSINESS", entityId: orgId, page, limit, dealCategoryId }));
    }
  }, [dispatch, orgId, page, dealCategoryId]);


  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];
    const keys = Object.keys(payload);
    const hasRmName = keys.some(k => k === 'rmName' || k === 'assignedRmName');
    const isDepositPayload = Boolean(payload.transactionId && (payload.purpose || payload.virtualAccountNumber));

    const isVisible = (key, value) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');

      if (
        lowerKey === 'previousstatus' ||
        lowerKey === 'updatedstatus' ||
        lowerKey === 'issuspicious' ||
        lowerKey === 'isinvestor' ||
        lowerKey === 'dealid' ||
        lowerKey === 'txnchannel' ||
        lowerKey === 'investorname'
      ) {
        return false;
      }
      if (value === null || value === undefined || value === '' || value === 'N/A') {
        return false;
      }

      if (
        lowerKey === 'investoruserid' ||
        lowerKey === 'invuserid' ||
        lowerKey === 'investorid' ||
        lowerKey === 'investoruser'
      ) {
        return false;
      }

      if (
        lowerKey === 'assigneeuserid' ||
        lowerKey === 'assigneduserid' ||
        lowerKey === 'assigneeid' ||
        lowerKey === 'assignedto'
      ) {
        return false;
      }

      if (
        lowerKey === 'entitytypeid' ||
        lowerKey === 'entitytype' ||
        lowerKey === 'businesstypeid' ||
        lowerKey === 'businesstype' ||
        lowerKey === 'investortypeid' ||
        lowerKey === 'usertypeid'
      ) {
        return false;
      }

      if (
        hasRmName &&
        (lowerKey === 'rmuserid' || lowerKey === 'rmid' || lowerKey === 'assignedrmuserid' || lowerKey === 'assignedrmid')
      ) {
        return false;
      }

      return true;
    };

    if (isDepositPayload) {
      return DEPOSIT_PAYLOAD_ORDER
        .filter((key) => Object.prototype.hasOwnProperty.call(payload, key) && isVisible(key, payload[key]))
        .map((key) => [key, payload[key]]);
    }

    return Object.entries(payload).filter(([key, value]) => isVisible(key, value));
  };

  const formatPayloadKey = (key) => {
    if (key === 'dealCategoryId') return 'Deal Category Name';
    if (key === 'busName' || key === 'businessName') return 'Business Name';
    if (key === 'businessTypeName' || key === 'businessType' || key === 'businessTypeId') return 'Business Type Name';
    if (key === 'rmName' || key === 'assignedRmName' || key === 'rmUserId' || key === 'rmId' || key === 'assignedRmUserId') return 'RM Name';
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
  };

  const getActionConfig = (action) => {
    switch (action) {
      case "NEW_ENQUIRY":
      case "ENQUIRY_CREATED": return { label: "Onboarding", color: "#e6ffed", textColor: "#22c55e" };
      case "COMPLETE_KYC": return { label: "KYC Completed", color: "#e6ffed", textColor: "#22c55e" };
      case "REVIEW_KYC": return { label: "KYC Reviewed", color: "#e6f7ff", textColor: "#1890ff" };
      case "ACTIVATE_USER": return { label: "User Activated", color: "#e6ffed", textColor: "#22c55e" };
      case "BUSINESS_PROFILE_UPDATED":
      case "INVESTOR_PROFILE_UPDATED": return { label: "Profile Update", color: "#e6f7ff", textColor: "#1890ff" };
      case "BUSINESS_DEACTIVATED":
      case "INVESTOR_DEACTIVATED": return { label: "Deactivation", color: "#fff0f6", textColor: "#eb2f96" };
      case "BUSINESS_BLACKLISTED":
      case "INVESTOR_BLACKLISTED": return { label: "Blacklist", color: "#fff1f0", textColor: "#f5222d" };
      case "BUSINESS_DELINQUENT":
      case "INVESTOR_DELINQUENT": return { label: "Delinquent", color: "#fffbe6", textColor: "#d4b106" };
      case "BUSINESS_REACTIVATED":
      case "INVESTOR_REACTIVATED": return { label: "Reactivation", color: "#e6ffed", textColor: "#22c55e" };
      case "RM_ASSIGNED": return { label: "RM Assigned", color: "#fff2e8", textColor: "#fa541c" };
      default: return { label: action.replace(/^BUSINESS_|^INVESTOR_/, "").replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const targetName = businessName || log.payload?.businessName || log.payload?.busName || "Business";

    if (log.action === "BUSINESS_PROFILE_UPDATED" || log.action === "INVESTOR_PROFILE_UPDATED") {
      return `<strong>${name}</strong> (${role}) <strong>UPDATED PROFILE</strong> for business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_DEACTIVATED" || log.action === "INVESTOR_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_BLACKLISTED" || log.action === "INVESTOR_BLACKLISTED") {
      return `<strong>${name}</strong> (${role}) <strong>BLACKLISTED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BUSINESS_DELINQUENT" || log.action === "INVESTOR_DELINQUENT") {
      return `<strong>${name}</strong> (${role}) <strong>MARKED</strong> business <strong>"${targetName}"</strong> as <strong>DELINQUENT</strong>`;
    }
    if (log.action === "BUSINESS_REACTIVATED" || log.action === "INVESTOR_REACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> business <strong>"${targetName}"</strong>`;
    }
    if (log.action === "RM_ASSIGNED") {
      const rm = log.payload?.rmName || "Relationship Manager";
      return `<strong>${name}</strong> (${role}) <strong>ASSIGNED RELATIONSHIP MANAGER</strong> <strong>"${rm}"</strong> to <strong>"${targetName}"</strong>`;
    }

    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on business <strong>"${targetName}"</strong>`;
  };

  return (
    <ActivityLogTimelineView
      title="Business Lifecycle History"
      totalRecords={totalRecords}
      loading={loading}
      logs={logs}
      page={page}
      setPage={setPage}
      limit={limit}
      getActionConfig={getActionConfig}
      formatDesc={formatDesc}
      filterPayloadEntries={filterPayloadEntries}
      formatPayloadKey={formatPayloadKey}
      formatPayloadValue={formatPayloadValue}
    />
  );
};

export default BusinessActivityLog;
